import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Users, CheckCircle, Clock, XCircle, Download, UserX, BarChart2, AlertCircle } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';
import { toast } from 'sonner';

const FormDashboard = () => {
  const navigate = useNavigate();
  const { formId } = useParams();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await api.get('/forms/' + formId + '/dashboard');
        setDashboard(response.data);
      } catch (error) {
        toast.error('Failed to load dashboard');
        navigate('/forms');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, [formId, navigate]);

  const handleExport = async () => {
    try {
      const response = await api.get('/forms/' + formId + '/export');
      const data = response.data;
      const csvRows = [data.columns.join(',')];
      data.rows.forEach(function(row) {
        const rowData = data.columns.map(function(col) {
          const val = row[col] || '';
          if (typeof val === 'string' && (val.indexOf(',') >= 0 || val.indexOf('"') >= 0)) {
            return '"' + val.replace(/"/g, '""') + '"';
          }
          return val;
        });
        csvRows.push(rowData.join(','));
      });
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = data.form_name.replace(/\s+/g, '_') + '_submissions.csv';
      link.click();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="p-6 flex items-center justify-center min-h-[50vh]">
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </AppLayout>
    );
  }

  if (!dashboard) return null;

  const stats = dashboard.stats;
  const submissionRate = stats.total_eligible > 0 ? Math.round((stats.submitted / stats.total_eligible) * 100) : 0;

  return (
    <AppLayout>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={function() { navigate('/forms'); }}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <BarChart2 className="w-6 h-6 text-[#4A9CC7]" />
                {dashboard.form_name}
              </h1>
              <p className="text-sm text-muted-foreground">Owner Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={dashboard.status === 'active' ? 'default' : 'secondary'}>
              {dashboard.status}
            </Badge>
            <Button variant="outline" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={function() { navigate('/forms/' + formId + '/submissions'); }} className="bg-[#4A9CC7] hover:bg-[#3d8ab5]">
              View Submissions
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Eligible Users</CardDescription>
              <CardTitle className="text-3xl flex items-center gap-2">
                <Users className="w-6 h-6 text-[#4A9CC7]" />
                {stats.total_eligible}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Users who can submit this form</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Submitted</CardDescription>
              <CardTitle className="text-3xl flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-500" />
                {stats.submitted}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{submissionRate}% submission rate</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Not Submitted</CardDescription>
              <CardTitle className="text-3xl flex items-center gap-2">
                <UserX className="w-6 h-6 text-gray-400" />
                {stats.not_submitted}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Users yet to respond</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-3 mb-6">
          <Card className="border-l-4 border-l-amber-500">
            <CardHeader>
              <CardDescription className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500" />
                Pending Approval
              </CardDescription>
              <CardTitle className="text-2xl">{stats.pending_approval}</CardTitle>
            </CardHeader>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardHeader>
              <CardDescription className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Approved
              </CardDescription>
              <CardTitle className="text-2xl">{stats.approved}</CardTitle>
            </CardHeader>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardHeader>
              <CardDescription className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                Rejected
              </CardDescription>
              <CardTitle className="text-2xl">{stats.rejected}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {dashboard.not_submitted_users && dashboard.not_submitted_users.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Users Who Have Not Submitted ({stats.not_submitted})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                {dashboard.not_submitted_users.map(function(user) {
                  return (
                    <div key={user.user_id} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium">
                        {user.name ? user.name.charAt(0) : '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{user.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
};

export default FormDashboard;
